import React, { useCallback, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Button, Snackbar, TextInput as PaperTextInput, useTheme } from 'react-native-paper';
import BaseBottomSheet from './BaseBottomSheet';
import { useAuth } from '../context/AuthContext';
import JobService from '../services/jobService';
import { ErrorLogger } from '../utils/errorLogger';

interface AddJobBottomSheetProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddJobBottomSheet: React.FC<AddJobBottomSheetProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const theme = useTheme();
  const { user } = useAuth();

  const [customerPhone, setCustomerPhone] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [amount, setAmount] = useState('');
  const [cargoTags, setCargoTags] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorSnackVisible, setErrorSnackVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const isSubmitEnabled =
    from.trim().length > 0 &&
    to.trim().length > 0 &&
    amount.trim().length > 0 &&
    cargoTags.trim().length > 0;

  const resetForm = useCallback(() => {
    setCustomerPhone('');
    setFrom('');
    setTo('');
    setAmount('');
    setCargoTags('');
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleSubmit = useCallback(async () => {
    if (!user?.uid) return;

    const tags = cargoTags
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);

    try {
      setSubmitting(true);
      await JobService.createJob(user.uid, {
        ...(customerPhone.trim().length > 0 && { customerPhone: customerPhone.trim() }),
        from,
        to,
        intakeAmount: parseFloat(amount),
        cargoTags: tags,
        status: 'pending',
        ...(user.displayName ? { createdByName: user.displayName } : {}),
      });
      resetForm();
      onSuccess();
      onClose();
    } catch (error) {
      ErrorLogger.logError(error as any, 'AddJobBottomSheet.handleSubmit');
      setErrorMessage('Failed to create job. Please try again.');
      setErrorSnackVisible(true);
    } finally {
      setSubmitting(false);
    }
  }, [user, customerPhone, from, to, amount, cargoTags, resetForm, onSuccess, onClose]);

  return (
    <BaseBottomSheet
      visible={visible}
      onClose={handleClose}
      title="New Job"
      height={0.75}
    >
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.fieldContainer}>
          <PaperTextInput
            mode="flat"
            label="Customer Phone"
            value={customerPhone}
            onChangeText={setCustomerPhone}
            placeholder="+256700000000"
            keyboardType="phone-pad"
            style={[styles.input, { backgroundColor: theme.colors.surfaceVariant }]}
            testID="input-phone"
          />
        </View>

        <View style={styles.fieldContainer}>
          <PaperTextInput
            mode="flat"
            label="From"
            value={from}
            onChangeText={setFrom}
            placeholder="Origin location"
            style={[styles.input, { backgroundColor: theme.colors.surfaceVariant }]}
            testID="input-from"
          />
        </View>

        <View style={styles.fieldContainer}>
          <PaperTextInput
            mode="flat"
            label="To"
            value={to}
            onChangeText={setTo}
            placeholder="Destination location"
            style={[styles.input, { backgroundColor: theme.colors.surfaceVariant }]}
            testID="input-to"
          />
        </View>

        <View style={styles.fieldContainer}>
          <PaperTextInput
            mode="flat"
            label="Negotiated Amount (UGX)"
            value={amount}
            onChangeText={setAmount}
            placeholder="0"
            keyboardType="numeric"
            style={[styles.input, { backgroundColor: theme.colors.surfaceVariant }]}
            testID="input-amount"
          />
        </View>

        <View style={styles.fieldContainer}>
          <PaperTextInput
            mode="flat"
            label="Cargo Tags"
            value={cargoTags}
            onChangeText={setCargoTags}
            placeholder="e.g. sand, timber"
            style={[styles.input, { backgroundColor: theme.colors.surfaceVariant }]}
            testID="input-tags"
          />
        </View>

      </ScrollView>

      <View style={styles.buttonContainer}>
        <Button
          mode="contained"
          onPress={handleSubmit}
          disabled={!isSubmitEnabled || submitting}
          loading={submitting}
          testID="submit-job-button"
        >
          {submitting ? 'Creating...' : 'Create Job'}
        </Button>
      </View>

      <Snackbar
        visible={errorSnackVisible}
        onDismiss={() => setErrorSnackVisible(false)}
        duration={3000}
        action={{ label: 'OK', onPress: () => setErrorSnackVisible(false) }}
      >
        {errorMessage}
      </Snackbar>
    </BaseBottomSheet>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flexShrink: 1,
  },
  scrollContent: {
    padding: 16,
  },
  fieldContainer: {
    marginBottom: 8,
  },
  input: {},
  buttonContainer: {
    padding: 16,
    paddingBottom: 16,
  },
});

export default AddJobBottomSheet;
